import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';
import { auth } from '../lib/firebase.js';
import { reconRoleFromClaims } from '../lib/roles.js';
import { DEMO, DEMO_PROFILE } from '../lib/demo.js';

const AuthContext = createContext(null);

// How often to force-refresh the ID token. The default token TTL is ~1h, which
// means custom-claim changes (role bumps, store reassignments, deactivation)
// can take up to an hour to land. Ten minutes is a friendly middle ground —
// short enough that an admin's role change is visible quickly, long enough
// not to hammer Firebase Auth's refresh quota.
const TOKEN_REFRESH_MS = 10 * 60 * 1000;

// Reads the SSO hash fragment, e.g. #portalToken=xxx&tenant=yyy&store=zzz, and
// returns the parsed token + tenant + store. Returns null if no token present.
function readPortalHash() {
  const hash = window.location.hash || '';
  if (!hash.startsWith('#') || !hash.includes('portalToken=')) return null;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('portalToken');
  if (!token) return null;
  return {
    token,
    tenantId: params.get('tenant') || null,
    storeId: params.get('store') || null,
  };
}

// Strip the hash from the URL so the JWT doesn't end up in browser history,
// referrers, or analytics. Called the moment we've consumed the token.
function clearHash() {
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } else {
    window.location.hash = '';
  }
}

function buildProfile(user, claims) {
  return {
    uid: user.uid,
    email: user.email,
    name: user.displayName || (user.email || '').split('@')[0],
    role: reconRoleFromClaims(claims),
    tenantId: claims.tenantId || null,
    storeId: claims.storeId || null,
    portalManaged: claims.issuedVia === 'portal',
  };
}

export function AuthProvider({ children }) {
  const [state, setState] = useState(
    DEMO
      ? {
          status: 'authenticated',
          user: { uid: DEMO_PROFILE.uid, email: DEMO_PROFILE.email },
          profile: DEMO_PROFILE,
          error: null,
        }
      : {
          status: 'loading', // 'loading' | 'authenticated' | 'unauthenticated' | 'error'
          user: null,        // firebase user
          profile: null,     // { email, name, role, tenantId, storeId, portalManaged }
          error: null,
        },
  );

  // True the moment we read a #portalToken= hash. If the mint subsequently
  // fails, the auth-state listener will see user=null and would normally
  // collapse our error state to 'unauthenticated', hiding the real failure.
  // This ref lets the listener preserve the error.
  const portalSsoAttemptedRef = useRef(false);

  // ---- 1. Consume the portal token if it's in the URL hash. ---------------
  useEffect(() => {
    if (DEMO) return;
    const bridge = readPortalHash();
    if (!bridge) return;

    portalSsoAttemptedRef.current = true;
    clearHash();
    signInWithCustomToken(auth, bridge.token).catch((err) => {
      console.error('[recon] portal SSO failed:', err);
      setState((s) => ({
        ...s,
        status: 'error',
        error:
          err?.code === 'auth/invalid-custom-token'
            ? 'Your launch link expired. Open Recon again from the portal.'
            : err?.message || 'Portal sign-in failed.',
      }));
    });
  }, []);

  // ---- 2. Subscribe to auth + token-change events. ------------------------
  // We use `onIdTokenChanged` instead of `onAuthStateChanged` so we get a
  // callback every time the ID token is refreshed (manually or by Firebase).
  // That keeps the profile in sync when custom claims change.
  useEffect(() => {
    if (DEMO) return;
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        // Critical: don't clobber a fresh portal-SSO error with a generic
        // "unauthenticated." The error explains *why* there's no user; if we
        // overwrite it the user is bounced to /login with no context. Only
        // collapse to unauthenticated when we weren't expecting a session.
        setState((s) => {
          if (s.status === 'error') return s; // preserve the error
          return { status: 'unauthenticated', user: null, profile: null, error: null };
        });
        return;
      }

      let claims = {};
      try {
        const tr = await user.getIdTokenResult();
        claims = tr.claims || {};
      } catch {
        // Proceed without claims; profile falls back to firebase user fields.
      }

      const profile = buildProfile(user, claims);
      setState({ status: 'authenticated', user, profile, error: null });
    });
    return unsub;
  }, []);

  // ---- 3. Periodic force-refresh of the ID token. -------------------------
  // Without this, a portal role change is invisible to recon for up to 1 hour.
  // Force-refresh every 10 minutes; the onIdTokenChanged listener above
  // automatically rebuilds the profile from the fresh claims.
  useEffect(() => {
    if (DEMO) return;
    if (state.status !== 'authenticated' || !state.user) return;
    const id = setInterval(() => {
      // Best-effort. If the network's down, the next refresh will retry.
      state.user.getIdToken(true).catch(() => {});
    }, TOKEN_REFRESH_MS);
    return () => clearInterval(id);
  }, [state.status, state.user]);

  const value = useMemo(
    () => ({
      ...state,
      signOut: () => signOut(auth),
      // Surface a hook for "I just did something privileged, give me a fresh
      // token now." Callers should use this after admin-side role changes.
      refreshClaims: async () => {
        if (state.user) {
          await state.user.getIdToken(true);
        }
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// Kept for compatibility — some code may still import `onAuthStateChanged`
// indirectly via this module. Marking it explicit to avoid a confusing
// "no export" error if anyone tries.
export { onAuthStateChanged };
